<script setup>
import { ref, computed } from 'vue';
import { BRAND_PRIMARY } from 'dashboard/constants/brandConstants';

const props = defineProps({
 form: {
 type: Object,
 required: true,
 },
});

const emit = defineEmits([
 'edit',
 'duplicate',
 'publish',
 'archive',
 'embed',
 'delete',
 'view-submissions',
]);

const showMenu = ref(false);

const statusConfig = computed(() => {
 const map = {
 draft: {
 label: 'Rascunho',
 dotClass: 'bg-amber-500',
 badgeClass:
 'bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-400',
 },
 published: {
 label: 'Publicado',
 dotClass: 'bg-green-500',
 badgeClass:
 'bg-green-50 dark:bg-green-500/10 text-green-700 dark:text-green-400',
 },
 archived: {
 label: 'Arquivado',
 dotClass: 'bg-n-slate-9',
 badgeClass:
 'bg-n-slate-3 text-n-slate-10',
 },
 };
 return map[props.form.status] || map.draft;
});

const submissionCount = computed(() => {
 return props.form.submissions_count || props.form.form_submissions_count || 0;
});

const fieldCount = computed(() => {
 return Array.isArray(props.form.fields)
 ? props.form.fields.length
 : 0;
});

const viewsCount = computed(() => props.form.views_count || 0);
const partialCount = computed(() => props.form.partial_submissions_count || 0);

const conversionRate = computed(() => {
 const views = viewsCount.value;
 const completed = submissionCount.value;
 if (views === 0) return null;
 return ((completed / views) * 100).toFixed(1);
});

function formatDate(dateStr) {
 if (!dateStr) return '--';
 return new Date(dateStr).toLocaleDateString('pt-BR', {
 day: '2-digit',
 month: 'short',
 });
}

function handleAction(action) {
 showMenu.value = false;
 emit(action, props.form.id);
}

function onClickOutside() {
 showMenu.value = false;
}
</script>

<template>
 <div
 class="group relative bg-n-solid-1 border border-n-weak rounded-xl hover:shadow-md hover:border-n-strong transition-all cursor-pointer"
 @click="$emit('edit', form.id)"
 >
 <!-- Card header with color accent -->
 <div
 class="h-1.5 rounded-t-xl"
 :style="{
 backgroundColor:
 form.settings?.primary_color || BRAND_PRIMARY,
 }"
 />

 <div class="p-4">
 <!-- Top row: Name + Menu -->
 <div class="flex items-start justify-between mb-2">
 <div class="min-w-0 flex-1">
 <h3
 class="text-sm font-semibold text-n-slate-12 truncate"
 >
 {{ form.name || 'Sem nome' }}
 </h3>
 <p
 v-if="form.description"
 class="text-xs text-n-slate-10 truncate !mb-0 mt-0.5"
 >
 {{ form.description }}
 </p>
 </div>

 <!-- Dropdown menu button -->
 <div class="relative ml-2" @click.stop>
 <button
 class="flex items-center justify-center size-7 rounded-lg text-n-slate-9 hover:text-n-slate-11 hover:bg-n-alpha-1 dark:hover:text-n-slate-7 transition-colors opacity-0 group-hover:opacity-100"
 @click="showMenu = !showMenu"
 >
 <span class="i-lucide-more-vertical size-4" />
 </button>

 <!-- Dropdown menu -->
 <Transition
 enter-active-class="transition-all duration-150"
 leave-active-class="transition-all duration-100"
 enter-from-class="opacity-0 scale-95"
 leave-to-class="opacity-0 scale-95"
 >
 <div
 v-if="showMenu"
 class="absolute right-0 top-full mt-1 w-44 bg-n-solid-1 border border-n-weak rounded-lg shadow-lg z-20 py-1"
 >
 <div
 class="fixed inset-0 z-[-1]"
 @click="onClickOutside"
 />
 <button
 class="flex items-center gap-2 w-full px-3 py-2 text-sm text-n-slate-11 hover:bg-n-alpha-1 transition-colors"
 @click="handleAction('edit')"
 >
 <span class="i-lucide-pencil size-3.5" />
 Editar
 </button>
 <button
 class="flex items-center gap-2 w-full px-3 py-2 text-sm text-n-slate-11 hover:bg-n-alpha-1 transition-colors"
 @click="handleAction('duplicate')"
 >
 <span class="i-lucide-copy size-3.5" />
 Duplicar
 </button>
 <button
 v-if="form.status !== 'published'"
 class="flex items-center gap-2 w-full px-3 py-2 text-sm text-n-slate-11 hover:bg-n-alpha-1 transition-colors"
 @click="handleAction('publish')"
 >
 <span class="i-lucide-globe size-3.5" />
 Publicar
 </button>
 <button
 v-if="form.status !== 'archived'"
 class="flex items-center gap-2 w-full px-3 py-2 text-sm text-n-slate-11 hover:bg-n-alpha-1 transition-colors"
 @click="handleAction('archive')"
 >
 <span class="i-lucide-archive size-3.5" />
 Arquivar
 </button>
 <button
 v-if="form.status === 'published'"
 class="flex items-center gap-2 w-full px-3 py-2 text-sm text-n-slate-11 hover:bg-n-alpha-1 transition-colors"
 @click="handleAction('embed')"
 >
 <span class="i-lucide-code size-3.5" />
 Codigo Embed
 </button>
 <button
 class="flex items-center gap-2 w-full px-3 py-2 text-sm text-n-slate-11 hover:bg-n-alpha-1 transition-colors"
 @click="handleAction('view-submissions')"
 >
 <span class="i-lucide-list size-3.5" />
 Submissoes
 </button>
 <div
 class="border-t border-n-weak my-1"
 />
 <button
 class="flex items-center gap-2 w-full px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors"
 @click="handleAction('delete')"
 >
 <span class="i-lucide-trash-2 size-3.5" />
 Excluir
 </button>
 </div>
 </Transition>
 </div>
 </div>

 <!-- Status badge -->
 <div class="flex items-center gap-2 mb-3">
 <span
 class="inline-flex items-center gap-1.5 text-[10px] font-semibold px-2 py-0.5 rounded-full"
 :class="statusConfig.badgeClass"
 >
 <span
 class="size-1.5 rounded-full"
 :class="statusConfig.dotClass"
 />
 {{ statusConfig.label }}
 </span>
 <span
 v-if="form.form_type === 'multi_step'"
 class="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-violet-50 dark:bg-violet-500/10 text-violet-700 dark:text-violet-400"
 >
 <span class="i-lucide-layers size-3" />
 Multi-etapa
 </span>
 </div>

 <!-- Stats -->
 <div
 class="flex items-center gap-3 text-xs text-n-slate-10 flex-wrap"
 >
 <div class="flex items-center gap-1">
 <span class="i-lucide-eye size-3" />
 {{ viewsCount }}
 </div>
 <div class="flex items-center gap-1">
 <span class="i-lucide-send size-3" />
 {{ submissionCount }}
 </div>
 <div
 v-if="partialCount > 0"
 class="flex items-center gap-1 text-amber-600 dark:text-amber-400"
 >
 <span class="i-lucide-pencil-line size-3" />
 {{ partialCount }}
 </div>
 <div
 v-if="conversionRate !== null"
 class="flex items-center gap-1 text-green-600 dark:text-green-400"
 >
 <span class="i-lucide-trending-up size-3" />
 {{ conversionRate }}%
 </div>
 <div class="flex items-center gap-1">
 <span class="i-lucide-calendar size-3" />
 {{ formatDate(form.created_at) }}
 </div>
 </div>
 </div>
 </div>
</template>
